import { useResolvedOwnerId } from '@/hooks/analytics/useResolvedOwnerId';
import { useAuth } from '@/hooks/use-auth';
import { useBrandKits } from '@/hooks/useBrandKits';
import { useShopifyConnection } from '@/hooks/useShopifyProducts';
import type { BusinessModel } from '@/lib/analytics/business-model';
import type { PlatformConnectionState } from '@/services/socialAuth/connection-state';

/**
 * The Products page is a Shopify catalog browser, so a workspace that neither
 * sells products nor holds a catalog has nothing to look at there. Services do
 * not get their own page: a service business's offerings already live in
 * Settings, Brand, "Products & services" (`brand_guidelines.offerings`), and a
 * second list would be two sources of truth.
 *
 * The row is shown when ANY of these holds:
 *  - the workspace has a catalog source: a Shopify connection bound to it,
 *    lapsed or active. Synced products only exist behind a bound connection,
 *    so a lapsed store is still a catalog worth showing;
 *  - `business_model` is `commerce`;
 *  - `business_model` is not classified yet.
 *
 * It is hidden only for a classified non-commerce workspace with no catalog.
 * The route itself stays reachable (old links, a store connected later) and
 * renders an honest empty state instead of a 404.
 *
 * `business_model` alone is unreliable, because the inference defaults most
 * workspaces to `commerce`. That failure direction is deliberate: a wrongly
 * shown row costs a click, a wrongly hidden one strands a merchant's catalog.
 */
export interface ProductsVisibilityInput {
  isLoading: boolean;
  businessModel: BusinessModel | null;
  connectionState: PlatformConnectionState;
}

export interface ProductsVisibility {
  visible: boolean;
  isLoading: boolean;
}

export function computeProductsVisibility({
  isLoading,
  businessModel,
  connectionState,
}: ProductsVisibilityInput): ProductsVisibility {
  if (isLoading) return { visible: false, isLoading: true };
  const hasCatalogSource = connectionState !== 'never_connected';
  const visible = hasCatalogSource || businessModel === null || businessModel === 'commerce';
  return { visible, isLoading: false };
}

/**
 * Resolves the active workspace and applies `computeProductsVisibility`.
 *
 * `visible` is never `true` while loading. Auth, the workspace pointer and the
 * kit list gate every case. The Shopify connection lookup only gates a
 * classified non-commerce workspace, the one case its answer can change, so a
 * commerce workspace never waits on it.
 */
export function useProductsVisibility(): ProductsVisibility {
  const { user, loading: authLoading } = useAuth();
  const { activeKit, isLoading: kitsLoading, isActiveKitPending } = useBrandKits();
  const ownerQuery = useResolvedOwnerId();
  const { connectionState, isConnectionLoading } = useShopifyConnection();

  const businessModel = activeKit?.business_model ?? null;
  const needsConnection = businessModel !== null && businessModel !== 'commerce';
  const identityLoading =
    authLoading || kitsLoading || isActiveKitPending || (Boolean(user?.id) && ownerQuery.isPending);
  const isLoading = identityLoading || (needsConnection && isConnectionLoading);

  return computeProductsVisibility({ isLoading, businessModel, connectionState });
}
